import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { HardHat, ShieldCheck, LogOut, Sparkles, Lock } from "lucide-react";
import { StripeCardForm } from "@/components/StripeCardForm";

export default function OnboardingCard() {
  const { user, refresh, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  // Admins and users who already have a card jump straight to the dashboard.
  useEffect(() => {
    if (loading) return;
    if (!user) navigate("/login", { replace: true });
    else if (!user.requires_card_setup || user.is_platform_admin) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const activateTrial = async (payment_method_id) => {
    setBusy(true);
    try {
      await api.post("/payments/start-trial", { payment_method_id, plan: "growth", interval: "monthly" });
      await refresh();
      toast.success("Your free trial is live — welcome to BidMatchAI.");
      navigate("/dashboard", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "We couldn't activate your trial. Please try a different card.");
      setBusy(false);
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-emerald-950">
        <div className="h-10 w-10 border-[3px] border-emerald-800 border-t-lime-300 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-emerald-950 flex flex-col items-center px-4 py-10" data-testid="onboarding-card-page">
      <div className="w-full max-w-lg">
        <div className="flex items-center justify-between mb-6 text-white">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-lime-400/20 flex items-center justify-center">
              <HardHat className="h-5 w-5 text-lime-300" strokeWidth={2.2} />
            </div>
            <span className="font-heading font-bold text-lg tracking-tight">BidMatchAI</span>
          </div>
          <button onClick={logout} data-testid="onboarding-logout" className="text-xs text-emerald-200/70 hover:text-white inline-flex items-center gap-1.5">
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>

        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          <div className="px-8 py-7 border-b border-slate-100">
            <div className="inline-flex items-center gap-2 text-[11px] font-semibold text-lime-700 uppercase tracking-wider mb-2">
              <Sparkles className="h-3.5 w-3.5" /> One quick step
            </div>
            <h1 className="font-heading text-2xl font-bold text-slate-900" data-testid="onboarding-heading">
              Add a card to activate your account
            </h1>
            <p className="text-sm text-slate-500 mt-1.5">
              Welcome back, {user.name || user.email}. We&apos;ve updated our policy — every account now needs a card on file to unlock the AI features. You get a <span className="font-semibold text-slate-800">5-day free trial</span>, and we won&apos;t charge you a cent until it ends.
            </p>
          </div>

          <div className="px-8 py-7 space-y-5">
            <div className="rounded-lg bg-emerald-50/60 border border-emerald-200 px-3 py-3 text-xs text-emerald-900 space-y-1">
              <p className="inline-flex items-center gap-2 font-semibold"><ShieldCheck className="h-4 w-4" /> What happens next</p>
              <ul className="pl-5 list-disc space-y-0.5 text-emerald-800">
                <li>Full access to opportunities, AI scoring, and bid triage for 5 days</li>
                <li>$50/mo starts after the trial ends unless you cancel</li>
                <li>Cancel anytime from Billing — no phone call, no email chain</li>
              </ul>
            </div>

            <StripeCardForm
              submitLabel="Start my 5-day free trial"
              note="Your card is encrypted and processed securely by Stripe. We never store card numbers."
              onConfirm={activateTrial}
            />
            {busy && <p className="text-xs text-slate-400 inline-flex items-center gap-1.5"><Lock className="h-3 w-3" /> Activating your trial…</p>}
          </div>
        </div>

        <p className="text-center text-xs text-emerald-200/60 mt-5">
          Questions? Email <a href="mailto:support@bidmatchai.io" className="underline hover:text-lime-300">support@bidmatchai.io</a>.
        </p>
      </div>
    </div>
  );
}
